import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { User, Phone, Clock, Calendar, TrendingUp, Tag, ArrowLeft, MapPin, FileText, Trash2, AlertTriangle, Edit2, Smartphone, IndianRupee, X, Receipt } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import { formatDate, formatTime, getStatusLabel, getTagColor } from '../utils/helpers';
import EditPatientTagsModal from './EditPatientTagsModal';
import RewardsTab from './RewardsTab';
import PatientPackagesSection from './PatientPackagesSection';
import PatientPharmacySalesSection from './PatientPharmacySalesSection';
import PatientCouponsSection from './PatientCouponsSection';
import PatientReviewPipelineSection from './PatientReviewPipelineSection';

const PatientProfileModal = ({ isOpen, onClose, patientId, user }) => {
  const [patient, setPatient] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTagsModal, setShowTagsModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [devices, setDevices] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', primary_phone: '', country_code: '+91', email: '', address: '', dob: '', gender: '', age: '' });
  const [saving, setSaving] = useState(false);
  const [ltv, setLtv] = useState(null);
  const [showLedger, setShowLedger] = useState(false);
  const [ledger, setLedger] = useState([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);

  useEffect(() => {
    if (patientId) {
      fetchPatientData();
    }
  }, [patientId]);

  const fetchPatientData = async () => {
    setLoading(true);
    try {
      const [patientRes, appointmentsRes, devicesRes, ltvRes] = await Promise.all([
        api.get(`/patients/${patientId}`),
        api.get(`/patients/${patientId}/appointments`),
        user?.role === 'Admin' ? api.get(`/admin/patients/${patientId}/devices`).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        user?.role === 'Admin'
          ? api.get(`/patients/${patientId}/ltv`).catch(() => ({ data: null }))
          : Promise.resolve({ data: null }),
      ]);
      setPatient(patientRes.data);
      setAppointments(appointmentsRes.data);
      setDevices(devicesRes.data || []);
      setLtv(ltvRes.data);
    } catch (error) {
      toast.error('Failed to fetch patient data');
    } finally {
      setLoading(false);
    }
  };

  const openLedger = async () => {
    setShowLedger(true);
    setLedgerLoading(true);
    try {
      const res = await api.get(`/patients/${patientId}/payments-ledger`);
      setLedger(res.data || []);
    } catch {
      toast.error('Could not load payment ledger');
    } finally {
      setLedgerLoading(false);
    }
  };

  const handleDeletePatient = async () => {
    if (!user || user.role !== 'Admin') {
      toast.error('Only admins can delete patients');
      return;
    }

    setDeleting(true);
    try {
      await api.delete(`/patients/${patientId}`);
      toast.success('Patient deleted successfully');
      setShowDeleteConfirm(false);
      onClose();
      // Optionally refresh the page or parent component
      window.location.reload();
    } catch (error) {
      console.error('Error deleting patient:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete patient');
    } finally {
      setDeleting(false);
    }
  };

  const handleOpenEditModal = () => {
    setEditForm({
      name: patient.name,
      primary_phone: patient.primary_phone,
      country_code: patient.country_code || '+91',
      email: patient.email || '',
      address: patient.address || '',
      dob: patient.dob || '',
      gender: patient.gender || '',
      age: patient.age != null ? String(patient.age) : '',
    });
    setShowEditModal(true);
  };

  const handleUpdatePatient = async () => {
    if (!editForm.name.trim()) {
      toast.error('Patient name is required');
      return;
    }
    if (!editForm.primary_phone.trim()) {
      toast.error('Phone number is required');
      return;
    }

    setSaving(true);
    try {
      await api.put(`/patients/${patientId}`, {
        name: editForm.name,
        primary_phone: editForm.primary_phone,
        country_code: editForm.country_code,
        email: editForm.email || null,
        address: editForm.address || null,
        dob: editForm.dob || null,
        gender: editForm.gender || null,
        age: editForm.age ? parseInt(editForm.age, 10) : null,
      });
      toast.success('Patient updated successfully');
      setShowEditModal(false);
      await fetchPatientData();
    } catch (error) {
      console.error('Error updating patient:', error);
      toast.error(error.response?.data?.detail || 'Failed to update patient');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-3xl">
          <div className="text-center py-12">Loading...</div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!patient) return null;

  // Delete confirmation dialog
  if (showDeleteConfirm) {
    return (
      <Dialog open={isOpen} onOpenChange={() => setShowDeleteConfirm(false)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-6 h-6" />
              Delete Patient
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-gray-700">
              Are you sure you want to delete <strong>{patient.name}</strong>?
            </p>
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-3">
              <p className="text-sm text-amber-800">
                <strong>⚠️ Warning:</strong> This action cannot be undone. All patient data including appointment history will remain but will be orphaned.
              </p>
            </div>
            <div className="bg-red-50 border border-red-300 rounded-lg p-3">
              <p className="text-sm text-red-800">
                <strong>Patient Details:</strong><br/>
                Name: {patient.name}<br/>
                Phone: {patient.primary_phone}<br/>
                Total Appointments: {appointments.length}
              </p>
            </div>
          </div>
          <div className="flex gap-3 justify-end">
            <Button
              onClick={() => setShowDeleteConfirm(false)}
              variant="outline"
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeletePatient}
              className="bg-red-600 hover:bg-red-700"
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Delete Patient'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // If an appointment is selected, show its details
  if (selectedAppointment) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => setSelectedAppointment(null)}
                  variant="ghost"
                  size="sm"
                  className="mr-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                </Button>
                <Calendar className="w-6 h-6 text-purple-600" />
                Appointment Details
              </div>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Patient Quick Info */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-4 rounded-lg">
              <div className="flex items-center gap-3">
                <User className="w-8 h-8 text-purple-600" />
                <div className="flex-1">
                  <h3 className="font-bold text-lg text-gray-900">{patient.name}</h3>
                  <p className="text-sm text-gray-600 flex items-center gap-2">
                    <Phone className="w-3 h-3" />
                    {patient.country_code || '+91'} {patient.primary_phone}
                  </p>
                  {(patient.email || patient.dob || patient.gender || patient.age != null || patient.address) && (
                    <div className="text-xs text-gray-600 mt-1 flex flex-wrap gap-x-3 gap-y-0.5" data-testid="patient-demographics">
                      {patient.email && <span>📧 {patient.email}</span>}
                      {patient.gender && <span>👤 {patient.gender}</span>}
                      {patient.dob && <span>🎂 {patient.dob}</span>}
                      {patient.age != null && <span>🎈 {patient.age} yrs</span>}
                      {patient.address && <span className="w-full">📍 {patient.address}</span>}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Appointment Information */}
            <div className="space-y-4">
              <div className="bg-white border border-purple-200 rounded-lg p-5">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h4 className="text-xl font-bold text-gray-900">{selectedAppointment.service_name}</h4>
                    <Badge className="mt-2 text-xs">
                      {getStatusLabel(selectedAppointment.status)}
                    </Badge>
                  </div>
                  {selectedAppointment.token_number && (
                    <div className="text-right">
                      <div className="text-xs text-gray-500 mb-1">Token Number</div>
                      <div className="text-2xl font-bold text-purple-600 font-mono">
                        {selectedAppointment.token_number}
                      </div>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div className="flex items-start gap-3">
                    <Calendar className="w-5 h-5 text-purple-600 mt-0.5" />
                    <div>
                      <div className="text-xs text-gray-500">Date</div>
                      <div className="font-semibold text-gray-900">
                        {formatDate(selectedAppointment.scheduled_date)}
                      </div>
                    </div>
                  </div>

                  {selectedAppointment.scheduled_time && (
                    <div className="flex items-start gap-3">
                      <Clock className="w-5 h-5 text-purple-600 mt-0.5" />
                      <div>
                        <div className="text-xs text-gray-500">Time</div>
                        <div className="font-semibold text-gray-900">
                          {formatTime(selectedAppointment.scheduled_time)}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Additional Details */}
              <div className="grid grid-cols-2 gap-4">
                {selectedAppointment.check_in_time && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                    <div className="text-xs text-emerald-600 font-semibold mb-1">Check-in Time</div>
                    <div className="text-sm text-gray-900">
                      {new Date(selectedAppointment.check_in_time).toLocaleTimeString('en-IN', {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true
                      })}
                    </div>
                  </div>
                )}

                {selectedAppointment.check_out_time && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="text-xs text-blue-600 font-semibold mb-1">Check-out Time</div>
                    <div className="text-sm text-gray-900">
                      {new Date(selectedAppointment.check_out_time).toLocaleTimeString('en-IN', {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true
                      })}
                    </div>
                  </div>
                )}

                {selectedAppointment.exposure_time_minutes && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="text-xs text-amber-600 font-semibold mb-1">Exposure Time</div>
                    <div className="text-sm text-gray-900">{Math.round(selectedAppointment.exposure_time_minutes)} minutes</div>
                  </div>
                )}

                {selectedAppointment.is_paid && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="text-xs text-green-600 font-semibold mb-1">Payment Status</div>
                    <div className="text-sm text-gray-900">✓ Paid</div>
                  </div>
                )}
              </div>

              {/* Remarks */}
              {selectedAppointment.remarks && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <FileText className="w-4 h-4 text-gray-600 mt-0.5" />
                    <div>
                      <div className="text-xs text-gray-500 font-semibold mb-1">Remarks</div>
                      <div className="text-sm text-gray-900">{selectedAppointment.remarks}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Action History */}
              {selectedAppointment.last_updated_by_user_name && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <div className="text-xs text-purple-600 font-semibold mb-2">Last Action By</div>
                  <div className="text-sm text-gray-900">
                    {selectedAppointment.last_updated_by_user_name}
                    {selectedAppointment.updated_at && (
                      <span className="text-gray-500 ml-2">
                        on {new Date(selectedAppointment.updated_at).toLocaleString()}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Default view: Patient profile with appointment list
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <User className="w-6 h-6 text-purple-600" />
              Patient Profile
            </div>
            {user && (user.role === 'Admin' || user.role === 'ADMIN') && (
              <Button
                onClick={() => setShowDeleteConfirm(true)}
                variant="outline"
                className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                size="sm"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Patient
              </Button>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Patient Info */}
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-xl">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-900">{patient.name}</h2>
                <div className="flex items-center gap-2 mt-2 text-gray-600">
                  <Phone className="w-4 h-4" />
                  <span>{patient.country_code || '+91'} {patient.primary_phone}</span>
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                {ltv && (
                  <button
                    onClick={openLedger}
                    data-testid="patient-ltv-badge"
                    className="group flex items-center gap-2 rounded-xl border border-emerald-300 bg-white px-3 py-1.5 shadow-sm hover:shadow-md hover:border-emerald-400 transition"
                    title="View full payment ledger"
                  >
                    <IndianRupee className="w-4 h-4 text-emerald-600" />
                    <div className="text-left leading-tight">
                      <div className="text-[10px] uppercase tracking-wide text-slate-500">Lifetime Value</div>
                      <div className="text-lg font-bold text-emerald-700">
                        ₹{(ltv.lifetime_value || 0).toLocaleString('en-IN')}
                      </div>
                    </div>
                    <Receipt className="w-3 h-3 text-slate-400 group-hover:text-emerald-600" />
                  </button>
                )}
                {patient.tags && patient.tags.length > 0 && (
                  <div className="flex gap-2 flex-wrap justify-end">
                    {patient.tags.map(tag => (
                      <Badge key={tag} className={`${getTagColor(tag)} text-sm px-3 py-1`}>
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <Button
                    onClick={handleOpenEditModal}
                    size="sm"
                    variant="outline"
                    className="border-blue-300 text-blue-600 hover:bg-blue-50"
                  >
                    <Edit2 className="w-4 h-4 mr-1" />
                    Edit Patient
                  </Button>
                  <Button
                    onClick={() => setShowTagsModal(true)}
                    size="sm"
                    variant="outline"
                    className="border-purple-300 text-purple-600 hover:bg-purple-50"
                  >
                    <Tag className="w-4 h-4 mr-1" />
                    Edit Tags
                  </Button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-4">
              <div className="bg-white/60 p-4 rounded-lg">
                <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                  <Clock className="w-4 h-4" />
                  <span>Average Lateness</span>
                </div>
                <div className="text-2xl font-bold text-amber-600">
                  {Math.round(patient.average_lateness || 0)} min
                </div>
              </div>
              <div className="bg-white/60 p-4 rounded-lg">
                <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                  <TrendingUp className="w-4 h-4" />
                  <span>Total Appointments</span>
                </div>
                <div className="text-2xl font-bold text-purple-600">
                  {appointments.length}
                </div>
              </div>
            </div>
          </div>

          {/* Eden Reward Points */}
          {(user?.role === 'Admin' || user?.role === 'FrontDeskExecutive') && (
            <RewardsTab patientId={patientId} user={user} onBooked={fetchPatientData} />
          )}

          {/* Packages */}
          <PatientPackagesSection patientId={patientId} user={user} />

          {/* Google-Review Discount Coupons */}
          <PatientCouponsSection patientId={patientId} />

          {/* Review Pipeline log — Admin & Feedback Reviewer only */}
          {(user?.role === 'Admin' || user?.role === 'FeedbackReviewer') && (
            <PatientReviewPipelineSection patientId={patientId} />
          )}

          {/* Pharmacy Sales (Admin-only; component self-hides for others via 403) */}
          <PatientPharmacySalesSection patientId={patientId} />

          {/* App & Devices */}
          {user?.role === 'Admin' && devices.length > 0 && (
            <div data-testid="patient-app-devices">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Smartphone className="w-5 h-5 text-indigo-600" />
                App & Devices
                <Badge variant="outline" className="text-xs">{devices.length}</Badge>
              </h3>
              <div className="space-y-2">
                {devices.map((d) => (
                  <div key={d.id} className="bg-indigo-50 border border-indigo-100 rounded-xl p-3 text-sm">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-900">{d.device_model || 'Unknown device'}</span>
                        <Badge className="bg-white text-slate-700 border border-slate-200 text-xs uppercase">{d.platform}</Badge>
                        {d.app_version && <Badge variant="outline" className="text-xs">v{d.app_version}</Badge>}
                        {d.push_enabled
                          ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs">Push ON</Badge>
                          : <Badge className="bg-slate-100 text-slate-500 border-slate-200 text-xs">Push OFF</Badge>}
                      </div>
                      <span className="text-xs text-slate-500">
                        Last active: {d.last_active_at ? new Date(d.last_active_at).toLocaleString() : '—'}
                      </span>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1">
                      First seen: {d.first_seen_at ? new Date(d.first_seen_at).toLocaleDateString() : '—'}
                      {d.os_version && <> · OS {d.os_version}</>}
                      <> · {d.session_count ?? 0} session{(d.session_count ?? 0) === 1 ? '' : 's'}</>
                      <span className="ml-2 font-mono text-slate-400">{d.device_id?.slice(0, 8)}…</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Appointment History */}
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-purple-600" />
              Appointment History
              {user?.role !== 'Admin' && (
                <span className="ml-2 text-xs font-normal text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full" data-testid="appointment-history-scope-note">
                  {user?.role === 'FrontDeskExecutive' ? 'Last 6 months + upcoming' : "Today's visits"}
                </span>
              )}
            </h3>
            {appointments.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {user?.role === 'Admin' ? 'No appointments yet' : 'No appointments in your visible window'}
              </div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {appointments.map(appointment => (
                  <button
                    key={appointment.id}
                    onClick={() => setSelectedAppointment(appointment)}
                    className="w-full bg-white border border-gray-200 p-4 rounded-lg hover:border-purple-300 hover:bg-purple-50 transition-all text-left group"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="font-semibold text-gray-900">
                          {appointment.service_name}
                        </div>
                        <div className="text-sm text-gray-600 mt-1">
                          {formatDate(appointment.scheduled_date)}
                          {appointment.scheduled_time && ` at ${formatTime(appointment.scheduled_time)}`}
                        </div>
                        {appointment.token_number && (
                          <div className="text-sm text-emerald-600 font-mono mt-1">
                            Token: {appointment.token_number}
                          </div>
                        )}
                        {appointment.exposure_time_minutes && (
                          <div className="text-xs text-gray-500 mt-1">
                            Exposure: {Math.round(appointment.exposure_time_minutes)} min
                          </div>
                        )}
                        {appointment.payment && (
                          <div
                            className="inline-flex items-center gap-1 mt-2 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5"
                            data-testid={`appt-payment-${appointment.id}`}
                          >
                            <IndianRupee className="w-3 h-3" />
                            {appointment.payment.amount.toLocaleString('en-IN')}
                            {appointment.payment.payment_method ? ` · ${appointment.payment.payment_method}` : ''}
                          </div>
                        )}
                      </div>
                      <Badge className="text-xs">
                        {getStatusLabel(appointment.status)}
                      </Badge>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>

      {/* Payments Ledger modal */}
      <Dialog open={showLedger} onOpenChange={setShowLedger}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="patient-ledger-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-emerald-600" />
              Payment Ledger — {patient?.name}
            </DialogTitle>
          </DialogHeader>

          {/* Stats strip */}
          {ltv && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 py-2">
              <LedgerStat label="Lifetime Value" value={`₹${(ltv.lifetime_value || 0).toLocaleString('en-IN')}`} color="text-emerald-700" />
              <LedgerStat label="Total Paid" value={`₹${(ltv.total_paid || 0).toLocaleString('en-IN')}`} color="text-slate-900" />
              <LedgerStat label="Refunded" value={`₹${(ltv.total_refunded || 0).toLocaleString('en-IN')}`} color="text-rose-700" />
              <LedgerStat label="Payments" value={ltv.payment_count || 0} color="text-purple-700" />
            </div>
          )}

          {ledgerLoading ? (
            <div className="text-center py-10 text-slate-500">Loading ledger…</div>
          ) : ledger.length === 0 ? (
            <div className="text-center py-10 text-slate-500">No payments recorded yet.</div>
          ) : (
            <div className="rounded-xl border border-slate-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="text-left px-3 py-2">Date</th>
                    <th className="text-left px-3 py-2">Receipt</th>
                    <th className="text-left px-3 py-2">Service / Linked Appt</th>
                    <th className="text-left px-3 py-2">Method</th>
                    <th className="text-right px-3 py-2">Amount</th>
                    <th className="text-left px-3 py-2">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {ledger.map((r) => (
                    <tr key={r.id} className="hover:bg-slate-50" data-testid={`ledger-row-${r.id}`}>
                      <td className="px-3 py-2 text-xs text-slate-500">
                        {r.created_at ? new Date(r.created_at).toLocaleString() : '—'}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{r.receipt_number || '—'}</td>
                      <td className="px-3 py-2">
                        <div className="text-sm text-slate-900">{r.service_name || '—'}</div>
                        {r.appointment_ref_number ? (
                          <div className="text-xs text-slate-500">Appt {r.appointment_ref_number}</div>
                        ) : (
                          <div className="text-xs text-slate-400 italic">Standalone payment</div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-700">{r.payment_method || '—'}</td>
                      <td className={`px-3 py-2 text-right font-semibold ${r.status === 'completed' ? 'text-emerald-700' : 'text-rose-700'}`}>
                        {r.status === 'completed' ? '+' : '−'}₹{(r.amount || 0).toLocaleString('en-IN')}
                      </td>
                      <td className="px-3 py-2">
                        <Badge className={
                          r.status === 'completed' ? 'bg-emerald-100 text-emerald-700 border-emerald-200 text-xs' :
                          r.status === 'cancelled' ? 'bg-rose-100 text-rose-700 border-rose-200 text-xs' :
                          'bg-slate-100 text-slate-600 border-slate-200 text-xs'
                        }>{r.status}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Patient Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-blue-600" />
              Edit Patient Details
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="edit-name">Patient Name</Label>
              <Input
                id="edit-name"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                placeholder="Enter patient name"
                className="mt-2"
              />
            </div>
            <div>
              <Label htmlFor="edit-country-code">Country Code</Label>
              <select
                id="edit-country-code"
                value={editForm.country_code}
                onChange={(e) => setEditForm({ ...editForm, country_code: e.target.value })}
                className="mt-2 flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              >
                <option value="+91">+91 (India)</option>
                <option value="+1">+1 (USA/Canada)</option>
                <option value="+44">+44 (UK)</option>
                <option value="+61">+61 (Australia)</option>
                <option value="+971">+971 (UAE)</option>
                <option value="+65">+65 (Singapore)</option>
                <option value="+60">+60 (Malaysia)</option>
                <option value="+82">+82 (South Korea)</option>
                <option value="+81">+81 (Japan)</option>
                <option value="+86">+86 (China)</option>
                <option value="+49">+49 (Germany)</option>
                <option value="+33">+33 (France)</option>
              </select>
            </div>
            <div>
              <Label htmlFor="edit-phone">Phone Number</Label>
              <Input
                id="edit-phone"
                type="tel"
                value={editForm.primary_phone}
                onChange={(e) => setEditForm({ ...editForm, primary_phone: e.target.value.replace(/\D/g, '') })}
                placeholder="Enter phone number"
                maxLength={10}
                className="mt-2"
              />
            </div>
            <div>
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                placeholder="patient@example.com"
                className="mt-2"
                data-testid="patient-edit-email"
              />
            </div>
            <div>
              <Label htmlFor="edit-address">Address</Label>
              <Textarea
                id="edit-address"
                value={editForm.address}
                onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                placeholder="House no, street, city, state, pincode"
                className="mt-2"
                rows={3}
                data-testid="patient-edit-address"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="edit-dob">Date of Birth (DD-MM-YYYY)</Label>
                <Input
                  id="edit-dob"
                  type="text"
                  value={editForm.dob}
                  onChange={(e) => {
                    let v = e.target.value.replace(/[^\d-]/g, '');
                    // Auto-insert hyphens as user types
                    if (v.length === 2 && editForm.dob.length < 2) v += '-';
                    else if (v.length === 5 && editForm.dob.length < 5) v += '-';
                    setEditForm({ ...editForm, dob: v.slice(0, 10), age: '' });
                  }}
                  placeholder="DD-MM-YYYY"
                  maxLength={10}
                  className="mt-2"
                  data-testid="patient-edit-dob"
                />
              </div>
              <div>
                <Label htmlFor="edit-age">Age{editForm.dob ? ' (from DOB)' : ''}</Label>
                <Input
                  id="edit-age"
                  type="number"
                  min="0"
                  max="120"
                  value={editForm.age}
                  onChange={(e) => setEditForm({ ...editForm, age: e.target.value })}
                  placeholder={editForm.dob ? 'Auto' : 'Enter age'}
                  disabled={!!editForm.dob}
                  className="mt-2"
                  data-testid="patient-edit-age"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="edit-gender">Gender</Label>
              <select
                id="edit-gender"
                value={editForm.gender}
                onChange={(e) => setEditForm({ ...editForm, gender: e.target.value })}
                className="mt-2 flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                data-testid="patient-edit-gender"
              >
                <option value="">Select…</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>
          <div className="flex gap-3 justify-end">
            <Button
              onClick={() => setShowEditModal(false)}
              variant="outline"
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdatePatient}
              className="bg-blue-600 hover:bg-blue-700"
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Tags Modal */}
      <EditPatientTagsModal
        isOpen={showTagsModal}
        onClose={() => setShowTagsModal(false)}
        patient={patient}
        onSuccess={fetchPatientData}
      />
    </Dialog>
  );
};

const LedgerStat = ({ label, value, color }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-2.5">
    <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
    <div className={`text-lg font-bold ${color}`}>{value}</div>
  </div>
);

export default PatientProfileModal;
